import { useMemo } from 'react';

import dayjs from 'dayjs';

import { DateRangeFilter, allFilters } from '@cast/shared-date-ui';
import { useClusters } from '@cast/shared-cluster-context-data-access';
import { useOrganizationOverviewQuery } from '@cast/shared-cost-data-access';
import {
  averageProvisionedCpuFromOverview,
  mapOverviewToFleetMetrics,
} from '@cast/features-cluster-shared-utils';
import type { Cluster } from '@cast/types';

import { getClustersOverviewRangeIso } from './useClustersOverviewDateRange';

export type ReportingTrialFleetStats = {
  avgProvisionedVcpu: number;
  clusterCount: number;
  nodeCount: number;
  clusters: Cluster[];
  isLoading: boolean;
  /** True when the organization overview request failed. */
  hasOverviewError: boolean;
};

/** Fleet metrics for reporting Status / Plans. Fixed last-30-days window. */
export const useReportingTrialFleetStats = (): ReportingTrialFleetStats => {
  const { clusters = [], isLoading: isClustersLoading } = useClusters({
    polling: true,
  });

  const rangeIso = useMemo(() => {
    const defaultRange = allFilters[DateRangeFilter.Last30Days].date(
      'UTC',
      false
    );
    return getClustersOverviewRangeIso([
      dayjs(defaultRange.from),
      dayjs(defaultRange.to),
    ]);
  }, []);

  const {
    data: overview,
    isPending: isOverviewPending,
    isError: hasOverviewError,
  } = useOrganizationOverviewQuery(rangeIso);

  const { stats } = useMemo(
    () => mapOverviewToFleetMetrics(overview, clusters),
    [clusters, overview]
  );

  const avgProvisionedVcpu = useMemo(
    () => averageProvisionedCpuFromOverview(overview),
    [overview]
  );

  return {
    avgProvisionedVcpu,
    clusterCount: stats.total,
    nodeCount: stats.totalNodes,
    clusters,
    isLoading: isClustersLoading || isOverviewPending,
    hasOverviewError,
  };
};
